import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PCB_CORE_URL } from "../../config";
import { useStore } from "../../store";
import "./quotefollowup.css";

const API_BASE = (PCB_CORE_URL || "").replace(/\/+$/, "");
const API_FOLLOWUP = (id) => `${API_BASE}/superuser/quotes/${id}/followup/`;

/** ----------------------------
 *  Helpers / empty models
 *  ---------------------------- */
function emptyBomRow() {
  return {
    bom_item_id: null,
    description: "",
    qty_per_board: 1,
    mpn: "", // "Reference fabricant"
    original_mpn: "",
    original_supplier: "",
    original_supplier_ref: "",
    supplier: "",
    supplier_ref: "", // "Ref fournisseur"
    assembly: "CMS",
    refs: "", // "Reperes"
    qty_ordered: 0, // "Quantite commandee"
    my12: false,
    support: false,
    mqt: false,

    client_choice: "standard",
    replacement_mpn: "",
    reason: "",
    pricing_blocked: false,
    blocking_reason: "",
  };
}

function normalizeClientChoice(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw || raw === "standard" || raw === "ok") return "standard";
  if (["fourni", "provided", "customer_supplied"].includes(raw)) return "Fourni";
  if (["supprimee", "supprimee", "not_assembled", "deleted"].includes(raw)) return "Supprimee";
  if (["remplace", "remplace", "replaced"].includes(raw)) return "Remplace";
  return value || "standard";
}

function clientChoiceLabel(value) {
  const choice = normalizeClientChoice(value);
  if (choice === "Fourni") return "Fourni";
  if (choice === "Supprimee") return "Supprimee";
  if (choice === "Remplace") return "Remplacee";
  return "Standard";
}

function isDeletedChoice(value) {
  return normalizeClientChoice(value) === "Supprimee";
}

function isProvidedChoice(value) {
  return normalizeClientChoice(value) === "Fourni";
}

function isReplacedChoice(value) {
  return normalizeClientChoice(value) === "Remplace";
}

function normalizeBomRow(row) {
  const next = { ...emptyBomRow(), ...(row || {}) };
  next.client_choice = normalizeClientChoice(next.client_choice);
  if (!next.reason && next.blocking_reason) next.reason = next.blocking_reason;
  return next;
}

function serializeBomRow(row) {
  const next = normalizeBomRow(row);
  return {
    ...next,
    client_choice: normalizeClientChoice(next.client_choice),
  };
}

function isMeaningfulBomChtRefRow(row) {
  const next = normalizeBomRow(row);
  const hasTextValue = [
    next.description,
    next.mpn,
    next.original_mpn,
    next.original_supplier,
    next.original_supplier_ref,
    next.supplier,
    next.supplier_ref,
    next.refs,
    next.replacement_mpn,
    next.reason,
    next.blocking_reason,
  ].some((value) => String(value || "").trim());

  if (hasTextValue) return true;
  if (next.my12 || next.support || next.mqt || next.pricing_blocked) return true;
  if (normalizeClientChoice(next.client_choice) !== "standard") return true;
  if (next.bom_item_id !== null && next.bom_item_id !== undefined) return true;

  const qtyPerBoard = String(next.qty_per_board ?? "").trim();
  if (qtyPerBoard && qtyPerBoard !== "1") return true;

  const qtyOrdered = String(next.qty_ordered ?? "").trim();
  if (qtyOrdered && qtyOrdered !== "0") return true;

  const assembly = String(next.assembly || "").trim().toUpperCase();
  if (assembly && assembly !== "CMS") return true;

  return false;
}

function bomDisplayRows(rows) {
  return (rows || [])
    .map((row, index) => ({ index, row: normalizeBomRow(row) }))
    .sort((a, b) => {
      const aDeleted = isDeletedChoice(a.row.client_choice) ? 1 : 0;
      const bDeleted = isDeletedChoice(b.row.client_choice) ? 1 : 0;
      if (aDeleted !== bDeleted) return aDeleted - bDeleted;
      const aProvided = isProvidedChoice(a.row.client_choice) ? 0 : 1;
      const bProvided = isProvidedChoice(b.row.client_choice) ? 0 : 1;
      if (aProvided !== bProvided) return aProvided - bProvided;
      const aSupplier = String(a.row.supplier || "").toLowerCase();
      const bSupplier = String(b.row.supplier || "").toLowerCase();
      if (aSupplier !== bSupplier) return aSupplier.localeCompare(bSupplier, "fr");

      const aId = Number(a.row.bom_item_id || 0);
      const bId = Number(b.row.bom_item_id || 0);
      if (aId !== bId) return aId - bId;
      return a.index - b.index;
    });
}

function bomRowClassName(row) {
  if (isDeletedChoice(row.client_choice)) return "boBomTraceRowDeleted";
  if (isReplacedChoice(row.client_choice)) return "boBomTraceRowReplaced";
  if (isProvidedChoice(row.client_choice)) return "boBomTraceRowProvided";
  return "";
}

function emptyModificationRow() {
  return {
    no: "",
    date: "",
    visa: "",
    remarks: "",
    meth: false,
    ce: false,
    cms: false,
    rep_ctrl_pth: false,
    expe: false,
    ok: false,
  };
}

function isMeaningfulModificationRow(row) {
  const next = row || {};
  return [
    next.no,
    next.date,
    next.visa,
    next.remarks,
  ].some((value) => String(value || "").trim()) || !!(next.meth || next.ce || next.cms || next.rep_ctrl_pth || next.expe || next.ok);
}

function emptyEtuvePcbPcbaRow() {
  return {
    in_date: "",
    in_time: "",
    in_visa: "",
    out_date: "",
    out_time: "",
    duration: "",
    out_visa: "",
    dry_date: "",
    dry_time: "",
    dry_visa: "",
  };
}

function emptyEtuveMslRow() {
  return {
    in_date: "",
    in_time: "",
    in_visa: "",
    theo_duration: "",
    out_date: "",
    out_time: "",
    duration: "",
    out_visa: "",
    dry_date: "",
    dry_time: "",
    dry_visa: "",
    open_drypack_date: "",
    open_drypack_time: "",
  };
}

function emptyEtuveUseMslRow() {
  return {
    out_dry_date: "",
    out_dry_time: "",
    out_dry_visa: "",
    exposure_hours: "",
    close_drypack_date: "",
    close_drypack_time: "",
    close_drypack_visa: "",
    etuve_date: "",
    etuve_time: "",
  };
}

export default function QuoteFollowup() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const access = useStore((s) => s.user.access);

  const apiFetch = useCallback(
    async (url, options = {}) => {
      return fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(access ? { Authorization: `Bearer ${access}` } : {}),
          ...(options.headers || {}),
        },
      });
    },
    [access]
  );

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [locked, setLocked] = useState(false);
  const [ofRef, setOfRef] = useState(String(quoteId));
  const [qty, setQty] = useState("1");
  const [tab, setTab] = useState("bom");

  /** ----------------------------
   *  Sheet models
   *  ---------------------------- */
  const [pcbClient, setPcbClient] = useState(false);
  const [pcbReinforced, setPcbReinforced] = useState(false);
  const [ctrlQtePcbPcba, setCtrlQtePcbPcba] = useState(false);
  const [bomRows, setBomRows] = useState([emptyBomRow()]);
  const [avantBac, setAvantBac] = useState({
    process_standard: true,
    type_of: "PCBA",
    type_process: "ROHS",
    classe_ipc: "2",
    exigences_client: "",
  });
  const [modsRows, setModsRows] = useState([]);
  const [etuve, setEtuve] = useState({
    pcb_pcba_rows: [emptyEtuvePcbPcbaRow()],
    msl_etuvage_rows: [emptyEtuveMslRow()],
    msl_use_rows: [emptyEtuveUseMslRow()],
  });
  const [bomChtRefRows, setBomChtRefRows] = useState([]);
  const [liaison, setLiaison] = useState({
    auteur_prog_pp: "",
    auteur_prog_jetting: "",
    jetting_critique: "",
    raison: "",
    liste_premount: "",
    observations_production: "",
  });
  const [sav, setSav] = useState({
    recp_cartes: "",
    date_recp_cartes: "",
    recp_cpt: "",
    date_recp_cpt: "",
    analyse: "",
    date_analyse: "",
    notes: "",
  });

  /** ----------------------------
   *  Load from API
   *  ---------------------------- */
  useEffect(() => {
    let alive = true;

    async function load() {
      setLoading(true);
      setErr("");
      try {
        const res = await apiFetch(API_FOLLOWUP(quoteId), { method: "GET" });
        if (!res.ok) {
          const t = await res.text().catch(() => "");
          throw new Error(`HTTP ${res.status} ${t}`.trim());
        }
        const data = await res.json();
        const f = data.followup_data || {};
        if (!alive) return;

        setLocked(!!data.locked);

        setOfRef(f.of_ref ?? String(quoteId));
        setQty(String(f.qty ?? "1"));
        setPcbClient(f.pcb_pcba?.pcb_client ?? false);
        setPcbReinforced(f.pcb_pcba?.pcb_reinforced ?? false);
        setCtrlQtePcbPcba(f.pcb_pcba?.ctrl_qte_pcb_pcba ?? false);
        setBomRows(Array.isArray(f.bom_rows) && f.bom_rows.length ? f.bom_rows.map(normalizeBomRow) : [emptyBomRow()]);
        setAvantBac({
          process_standard: f.avant_bac?.process_standard ?? true,
          type_of: f.avant_bac?.type_of ?? "PCBA",
          type_process: f.avant_bac?.type_process ?? "ROHS",
          classe_ipc: String(f.avant_bac?.classe_ipc ?? "2"),
          exigences_client: f.avant_bac?.exigences_client ?? "",
        });
        setModsRows(
          Array.isArray(f.suivi_modifications)
            ? f.suivi_modifications.filter(isMeaningfulModificationRow)
            : []
        );
        setEtuve({
          pcb_pcba_rows:
            Array.isArray(f.etuve?.pcb_pcba_rows) && f.etuve.pcb_pcba_rows.length
              ? f.etuve.pcb_pcba_rows
              : [emptyEtuvePcbPcbaRow()],
          msl_etuvage_rows:
            Array.isArray(f.etuve?.msl_etuvage_rows) && f.etuve.msl_etuvage_rows.length
              ? f.etuve.msl_etuvage_rows
              : [emptyEtuveMslRow()],
          msl_use_rows:
            Array.isArray(f.etuve?.msl_use_rows) && f.etuve.msl_use_rows.length
              ? f.etuve.msl_use_rows
              : [emptyEtuveUseMslRow()],
        });
        setBomChtRefRows(
          Array.isArray(f.bom_cht_ref_rows)
            ? f.bom_cht_ref_rows.map(normalizeBomRow).filter(isMeaningfulBomChtRefRow)
            : []
        );
        setLiaison({
          auteur_prog_pp: f.fiche_liaison?.auteur_prog_pp ?? "",
          auteur_prog_jetting: f.fiche_liaison?.auteur_prog_jetting ?? "",
          jetting_critique: f.fiche_liaison?.jetting_critique ?? "",
          raison: f.fiche_liaison?.raison ?? "",
          liste_premount: f.fiche_liaison?.liste_premount ?? "",
          observations_production: f.fiche_liaison?.observations_production ?? "",
        });
        setSav({
          recp_cartes: f.fiche_sav?.recp_cartes ?? "",
          date_recp_cartes: f.fiche_sav?.date_recp_cartes ?? "",
          recp_cpt: f.fiche_sav?.recp_cpt ?? "",
          date_recp_cpt: f.fiche_sav?.date_recp_cpt ?? "",
          analyse: f.fiche_sav?.analyse ?? "",
          date_analyse: f.fiche_sav?.date_analyse ?? "",
          notes: f.fiche_sav?.notes ?? "",
        });
      } catch (e) {
        if (alive) setErr(e?.message || "Erreur.");
      } finally {
        if (alive) setLoading(false);
      }
    }

    load();
    return () => (alive = false);
  }, [apiFetch, quoteId]);

  /** ----------------------------
   *  Save payload (ALL sheets)
   *  ---------------------------- */
  async function onSave() {
    setSaving(true);
    setErr("");
    try {
      const payload = {
        followup_data: {
          of_ref: ofRef,
          qty: Number(qty || 0),

          pcb_pcba: {
            pcb_client: !!pcbClient,
            pcb_reinforced: !!pcbReinforced,
            ctrl_qte_pcb_pcba: !!ctrlQtePcbPcba,
          },

          bom_rows: bomRows.map(serializeBomRow),

          avant_bac: {
            process_standard: !!avantBac.process_standard,
            type_of: avantBac.type_of,
            type_process: avantBac.type_process,
            classe_ipc: avantBac.classe_ipc,
            exigences_client: avantBac.exigences_client,
          },

          suivi_modifications: (modsRows || []).filter(isMeaningfulModificationRow),

          etuve: etuve,

          bom_cht_ref_rows: (bomChtRefRows || []).map(serializeBomRow).filter(isMeaningfulBomChtRefRow),

          fiche_liaison: liaison,

          fiche_sav: sav,
        },
      };

      const res = await apiFetch(API_FOLLOWUP(quoteId), {
        method: "PUT",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`.trim());
      }

      const data = await res.json();
      setLocked(!!data.locked);
    } catch (e) {
      setErr(e?.message || "Erreur save.");
    } finally {
      setSaving(false);
    }
  }

  /** ----------------------------
   *  Generic list updaters
   *  ---------------------------- */
  function updateListRow(setter, i, key, value) {
    setter((prev) => (prev || []).map((r, idx) => (idx === i ? { ...r, [key]: value } : r)));
  }

  function addListRow(setter, rowFactory) {
    setter((prev) => [...(prev || []), rowFactory()]);
  }

  function removeListRow(setter, i) {
    setter((prev) => (prev || []).filter((_, idx) => idx !== i));
  }

  if (loading) return <div className="boCard">Chargement...</div>;

  const displayedBomRows = bomDisplayRows(bomRows);

  return (
    <div className="boFU">
      <div className="boFUHeader">
        <div>
          <h1 className="boFUTitle">Fiche suiveuse: Devis {quoteId}</h1>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="boBtn boBtnGhost" onClick={() => navigate(-1)}>
            Retour
          </button>
          <a
            className="boBtn boBtnGhost"
            href={`${API_BASE}/superuser/quotes/${quoteId}/customer-files/`}
            style={{ textDecoration: "none" }}
            title="Télécharger les fichiers déposés par le client (ZIP)"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const resp = await fetch(`${API_BASE}/superuser/quotes/${quoteId}/customer-files/`, {
                  headers: { Authorization: `Bearer ${access}` },
                });
                if (!resp.ok) {
                  const t = await resp.text().catch(() => "");
                  alert(`Erreur: ${resp.status} ${t}`);
                  return;
                }
                const blob = await resp.blob();
                const href = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = href;
                a.download = `fichiers_client_devis_${quoteId}.zip`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(href);
              } catch (err) {
                alert(err?.message || "Erreur téléchargement");
              }
            }}
          >
            Fichiers client (ZIP)
          </a>
          <a
            className="boBtn boBtnGhost"
            href="#"
            style={{ textDecoration: "none" }}
            title="Télécharger la fiche suiveuse au format Excel"
            onClick={async (e) => {
              e.preventDefault();
              try {
                const resp = await fetch(`${API_BASE}/superuser/quotes/${quoteId}/fiche-suiveuse/`, {
                  headers: { Authorization: `Bearer ${access}` },
                });
                if (!resp.ok) {
                  const t = await resp.text().catch(() => "");
                  alert(`Erreur: ${resp.status} ${t}`);
                  return;
                }
                const blob = await resp.blob();
                const href = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = href;
                a.download = `fiche_suiveuse_devis_${quoteId}.xlsx`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(href);
              } catch (err) {
                alert(err?.message || "Erreur téléchargement Excel");
              }
            }}
          >
            Fiche suiveuse (Excel)
          </a>
          <button className="boBtn boBtnPrimary" onClick={onSave} disabled={saving || locked}>
            {locked ? "Verrouillé" : saving ? "Sauvegarde..." : "Enregistrer"}
          </button>
        </div>
      </div>

      {err ? (
        <div className="boCard boError">
          <div className="boErrorTitle">Erreur</div>
          <div className="boErrorText">{err}</div>
        </div>
      ) : null}

      {/* Header + Tabs */}
      <div className="boCard" style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <label className="boLabel">
          OF
          <input className="boInput mono" value={ofRef} onChange={(e) => setOfRef(e.target.value)} disabled={locked} />
        </label>
        <label className="boLabel">
          Quantité
          <input className="boInput mono" value={qty} onChange={(e) => setQty(e.target.value)} disabled={locked} />
        </label>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {[
            ["bom", "BOM"],
            ["pcbpcba", "FICHE PCB-PCBA"],
            ["avant", "FICHE AVANT BAC"],
            ["mods", "SUIVI MODIFICATIONS"],
            ["etuve", "FS ETUVE"],
            ["bomchtref", "BOM CHT REF"],
            ["liaison", "FICHE LIAISON"],
            ["sav", "FICHE SAV"],
          ].map(([k, label]) => (
            <button key={k} className={`boBtn ${tab === k ? "boBtnPrimary" : ""}`} onClick={() => setTab(k)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* TAB: FICHE PCB-PCBA */}
      {tab === "pcbpcba" ? (
        <div className="boCard">
          <div className="boSectionTitle">FICHE PCB-PCBA</div>

          <label className="boRow" style={{ gap: 8 }}>
            <input type="checkbox" checked={ctrlQtePcbPcba} onChange={(e) => setCtrlQtePcbPcba(e.target.checked)} disabled={locked} />
            CTRL QTE PCB/PCBA
          </label>

          <label className="boRow" style={{ gap: 8 }}>
            <input type="checkbox" checked={pcbReinforced} onChange={(e) => setPcbReinforced(e.target.checked)} disabled={locked} />
            CTRL PCB RENFORCE
          </label>

          <label className="boRow" style={{ gap: 8 }}>
            <input type="checkbox" checked={pcbClient} onChange={(e) => setPcbClient(e.target.checked)} disabled={locked} />
            PCB CLIENT (OUI/NON)
          </label>

          {/* <div className="boHint" style={{ marginTop: 8 }}>
            (Excel : flags sur "FICHE PCB-PCBA". Ici : stockes dans <code>followup_data.pcb_pcba</code>.)
          </div> */}
        </div>
      ) : null}

      {/* TAB: BOM */}
      {tab === "bom" ? (
        <div className="boCard">
          <div className="boSectionTitle" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>BOM</span>
            <button className="boBtn" onClick={() => addListRow(setBomRows, emptyBomRow)} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          <div className="boHint" style={{ marginBottom: 10 }}>
            Les références supprimées restent visibles en bas afin de conserver une trace des modifications effectuées par le client.
          </div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="right">Qte/carte</th>
                  <th>MPN actif</th>
                  <th>Fournisseur</th>
                  <th>Réf fournisseur</th>
                  <th>Assemblage</th>
                  <th>Repères</th>
                  <th className="right">Qté commandée</th>
                  <th className="center">MY12</th>
                  <th className="center">SUPPORT</th>
                  <th className="center">MQT</th>
                  <th>Choix client</th>
                  <th>Ancien MPN</th>
                  <th>Anc. fournisseur</th>
                  <th>Anc. ref fournisseur</th>
                  <th>Nouveau MPN</th>
                  <th>Motif</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayedBomRows.map(({ row: r, index: i }) => (
                  <tr key={r.bom_item_id || i} className={bomRowClassName(r)}>
                    <td>
                      <input className="boInput" value={r.description} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "description", e.target.value)} />
                    </td>
                    <td className="right">
                      <input className="boInput mono" value={r.qty_per_board} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "qty_per_board", e.target.value)} />
                    </td>
                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellNew" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputNew" : ""}`} value={r.mpn} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "mpn", e.target.value)} />
                    </td>
                    <td>
                      <input className="boInput mono" value={r.supplier} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "supplier", e.target.value)} />
                    </td>
                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellNew" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputNew" : ""}`} value={r.supplier_ref} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "supplier_ref", e.target.value)} />
                    </td>
                    <td>
                      <input className="boInput mono" value={r.assembly} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "assembly", e.target.value)} />
                    </td>
                    <td>
                      <input className="boInput mono" value={r.refs} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "refs", e.target.value)} />
                    </td>
                    <td className="right">
                      <input className="boInput mono" value={r.qty_ordered} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "qty_ordered", e.target.value)} />
                    </td>

                    <td className="center">
                      <input type="checkbox" checked={!!r.my12} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "my12", e.target.checked)} />
                    </td>
                    <td className="center">
                      <input type="checkbox" checked={!!r.support} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "support", e.target.checked)} />
                    </td>
                    <td className="center">
                      <input type="checkbox" checked={!!r.mqt} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "mqt", e.target.checked)} />
                    </td>

                    <td>
                      <select className="boSelect" value={normalizeClientChoice(r.client_choice)} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "client_choice", normalizeClientChoice(e.target.value))}>
                        <option value="standard">standard</option>
                        <option value="Fourni">fourni</option>
                        <option value="Remplace">Remplacé</option>
                        <option value="Supprimee">Supprimée</option>
                      </select>
                      <div className="boHint" style={{ marginTop: 4 }}>{clientChoiceLabel(r.client_choice)}</div>
                    </td>

                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellOld" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputOld" : ""}`} value={r.original_mpn || ""} disabled onChange={() => {}} />
                    </td>
                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellOld" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputOld" : ""}`} value={r.original_supplier || ""} disabled onChange={() => {}} />
                    </td>
                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellOld" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputOld" : ""}`} value={r.original_supplier_ref || ""} disabled onChange={() => {}} />
                    </td>
                    <td className={isReplacedChoice(r.client_choice) ? "boBomTraceCellNew" : ""}>
                      <input className={`boInput mono ${isReplacedChoice(r.client_choice) ? "boBomTraceInputNew" : ""}`} value={r.replacement_mpn} disabled={locked || !isReplacedChoice(r.client_choice)} onChange={(e) => updateListRow(setBomRows, i, "replacement_mpn", e.target.value)} />
                    </td>

                    <td>
                      <input className="boInput" value={r.reason} disabled={locked} onChange={(e) => updateListRow(setBomRows, i, "reason", e.target.value)} />
                      {r.blocking_reason && !r.reason ? <div className="boHint" style={{ marginTop: 4 }}>{r.blocking_reason}</div> : null}
                    </td>

                    <td className="right">
                      <button className="boBtn boBtnGhost" onClick={() => removeListRow(setBomRows, i)} disabled={locked}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {!bomRows.length ? <tr><td colSpan={18} style={{ opacity: 0.8 }}>Aucune ligne</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
      {/* TAB: FICHE AVANT BAC */}
      {tab === "avant" ? (
        <div className="boCard">
          <div className="boSectionTitle">FICHE AVANT BAC</div>

          <label className="boRow" style={{ gap: 8 }}>
            <input
              type="checkbox"
              checked={!!avantBac.process_standard}
              onChange={(e) => setAvantBac((p) => ({ ...p, process_standard: e.target.checked }))}
              disabled={locked}
            />
            PROCESS STANDARD
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(220px, 1fr))", gap: 12, marginTop: 10 }}>
            <label className="boLabel">
              TYPE OF
              <input className="boInput mono" value={avantBac.type_of} disabled={locked} onChange={(e) => setAvantBac((p) => ({ ...p, type_of: e.target.value }))} />
            </label>
            <label className="boLabel">
              TYPE PROCESS
              <input className="boInput mono" value={avantBac.type_process} disabled={locked} onChange={(e) => setAvantBac((p) => ({ ...p, type_process: e.target.value }))} />
            </label>
            <label className="boLabel">
              CLASSE IPC
              <input className="boInput mono" value={avantBac.classe_ipc} disabled={locked} onChange={(e) => setAvantBac((p) => ({ ...p, classe_ipc: e.target.value }))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint" style={{ marginBottom: 6 }}>
              EXIGENCES CLIENT HORS-STANDARD :
            </div>
            <textarea
              className="boInput"
              style={{ minHeight: 140, resize: "vertical" }}
              value={avantBac.exigences_client}
              disabled={locked}
              onChange={(e) => setAvantBac((p) => ({ ...p, exigences_client: e.target.value }))}
            />
          </div>
        </div>
      ) : null}

      {/* TAB: SUIVI MODIFICATIONS */}
      {tab === "mods" ? (
        <div className="boCard">
          <div className="boSectionTitle" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>SUIVI MODIFICATIONS</span>
            <button className="boBtn" onClick={() => addListRow(setModsRows, emptyModificationRow)} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>No</th>
                  <th style={{ width: 130 }}>Date</th>
                  <th style={{ width: 90 }}>Visa</th>
                  <th>Remarques / Modifications / Rework</th>
                  <th className="center">METH</th>
                  <th className="center">CE</th>
                  <th className="center">CMS</th>
                  <th className="center">REP CTRL PTH</th>
                  <th className="center">EXPE</th>
                  <th className="center">OK</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {modsRows.map((r, i) => (
                  <tr key={i}>
                    <td><input className="boInput mono" value={r.no} disabled={locked} onChange={(e) => updateListRow(setModsRows, i, "no", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.date} disabled={locked} onChange={(e) => updateListRow(setModsRows, i, "date", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.visa} disabled={locked} onChange={(e) => updateListRow(setModsRows, i, "visa", e.target.value)} /></td>
                    <td><input className="boInput" value={r.remarks} disabled={locked} onChange={(e) => updateListRow(setModsRows, i, "remarks", e.target.value)} /></td>

                    {["meth", "ce", "cms", "rep_ctrl_pth", "expe", "ok"].map((k) => (
                      <td key={k} className="center">
                        <input type="checkbox" checked={!!r[k]} disabled={locked} onChange={(e) => updateListRow(setModsRows, i, k, e.target.checked)} />
                      </td>
                    ))}

                    <td className="right">
                      <button className="boBtn boBtnGhost" onClick={() => removeListRow(setModsRows, i)} disabled={locked}>Supprimer</button>
                    </td>
                  </tr>
                ))}
                {!modsRows.length ? <tr><td colSpan={11} style={{ opacity: 0.8 }}>Aucune ligne</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* TAB: FS ETUVE */}
      {tab === "etuve" ? (
        <div className="boCard">
          <div className="boSectionTitle">FS ETUVE</div>

          {/* <div className="boHint" style={{ marginBottom: 10 }}>
            ETUVAGE PCB/PCBA (Excel : Entree etuve / Sortie etuve / Mise en armoire seche)
          </div> */}

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div />
            <button className="boBtn" onClick={() => setEtuve((p) => ({ ...p, pcb_pcba_rows: [...(p.pcb_pcba_rows || []), emptyEtuvePcbPcbaRow()] }))} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th colSpan={3} className="center">Entree etuve</th>
                  <th colSpan={4} className="center">Sortie etuve</th>
                  <th colSpan={3} className="center">Mise en armoire seche</th>
                  <th className="right">Actions</th>
                </tr>
                <tr>
                  <th>Date</th><th>Heure</th><th>Visa</th>
                  <th>Date</th><th>Heure</th><th>Duree</th><th>Visa</th>
                  <th>Date</th><th>Heure</th><th>Visa</th>
                  <th className="right"></th>
                </tr>
              </thead>
              <tbody>
                {(etuve.pcb_pcba_rows || []).map((r, i) => (
                  <tr key={i}>
                    {["in_date","in_time","in_visa","out_date","out_time","duration","out_visa","dry_date","dry_time","dry_visa"].map((k) => (
                      <td key={k}><input className="boInput mono" value={r[k] || ""} disabled={locked} onChange={(e) => {
                        setEtuve((p) => ({
                          ...p,
                          pcb_pcba_rows: (p.pcb_pcba_rows || []).map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)),
                        }));
                      }} /></td>
                    ))}
                    <td className="right">
                      <button className="boBtn boBtnGhost" disabled={locked} onClick={() => {
                        setEtuve((p) => ({ ...p, pcb_pcba_rows: (p.pcb_pcba_rows || []).filter((_, idx) => idx !== i) }));
                      }}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr style={{ opacity: 0.15, margin: "18px 0" }} />

          {/* <div className="boHint" style={{ marginBottom: 10 }}>
            ETUVAGE COMPOSANTS MSL >= 3 (Excel : entree 125C / sortie / armoire seche / ouverture drypack)
          </div> */}

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div />
            <button className="boBtn" onClick={() => setEtuve((p) => ({ ...p, msl_etuvage_rows: [...(p.msl_etuvage_rows || []), emptyEtuveMslRow()] }))} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th colSpan={4} className="center">Entree etuve 125C</th>
                  <th colSpan={4} className="center">Sortie etuve</th>
                  <th colSpan={3} className="center">Armoire seche</th>
                  <th colSpan={2} className="center">Ouverture Drypack</th>
                  <th className="right">Actions</th>
                </tr>
                <tr>
                  <th>Date</th><th>Heure</th><th>Visa</th><th>Duree theorique</th>
                  <th>Date</th><th>Heure</th><th>Duree</th><th>Visa</th>
                  <th>Date</th><th>Heure</th><th>Visa</th>
                  <th>Date</th><th>Heure</th>
                  <th className="right"></th>
                </tr>
              </thead>
              <tbody>
                {(etuve.msl_etuvage_rows || []).map((r, i) => (
                  <tr key={i}>
                    {[
                      "in_date","in_time","in_visa","theo_duration",
                      "out_date","out_time","duration","out_visa",
                      "dry_date","dry_time","dry_visa",
                      "open_drypack_date","open_drypack_time",
                    ].map((k) => (
                      <td key={k}>
                        <input className="boInput mono" value={r[k] || ""} disabled={locked} onChange={(e) => {
                          setEtuve((p) => ({
                            ...p,
                            msl_etuvage_rows: (p.msl_etuvage_rows || []).map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)),
                          }));
                        }} />
                      </td>
                    ))}
                    <td className="right">
                      <button className="boBtn boBtnGhost" disabled={locked} onClick={() => {
                        setEtuve((p) => ({ ...p, msl_etuvage_rows: (p.msl_etuvage_rows || []).filter((_, idx) => idx !== i) }));
                      }}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <hr style={{ opacity: 0.15, margin: "18px 0" }} />

          {/* <div className="boHint" style={{ marginBottom: 10 }}>
            UTILISATION COMPOSANTS MSL >= 3 (Excel : sortie armoire / calcul exposition / fermeture drypack / etuvage)
          </div> */}

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
            <div />
            <button className="boBtn" onClick={() => setEtuve((p) => ({ ...p, msl_use_rows: [...(p.msl_use_rows || []), emptyEtuveUseMslRow()] }))} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th colSpan={3} className="center">Sortie armoire seche</th>
                  <th className="center">Temps exposition</th>
                  <th colSpan={3} className="center">Fermeture Drypack</th>
                  <th colSpan={2} className="center">Etuvage</th>
                  <th className="right">Actions</th>
                </tr>
                <tr>
                  <th>Date</th><th>Heure</th><th>Visa</th>
                  <th>Nb heures</th>
                  <th>Date</th><th>Heure</th><th>Visa</th>
                  <th>Date</th><th>Heure</th>
                  <th className="right"></th>
                </tr>
              </thead>
              <tbody>
                {(etuve.msl_use_rows || []).map((r, i) => (
                  <tr key={i}>
                    {[
                      "out_dry_date","out_dry_time","out_dry_visa",
                      "exposure_hours",
                      "close_drypack_date","close_drypack_time","close_drypack_visa",
                      "etuve_date","etuve_time",
                    ].map((k) => (
                      <td key={k}>
                        <input className="boInput mono" value={r[k] || ""} disabled={locked} onChange={(e) => {
                          setEtuve((p) => ({
                            ...p,
                            msl_use_rows: (p.msl_use_rows || []).map((x, idx) => (idx === i ? { ...x, [k]: e.target.value } : x)),
                          }));
                        }} />
                      </td>
                    ))}
                    <td className="right">
                      <button className="boBtn boBtnGhost" disabled={locked} onClick={() => {
                        setEtuve((p) => ({ ...p, msl_use_rows: (p.msl_use_rows || []).filter((_, idx) => idx !== i) }));
                      }}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* TAB: BOM CHT REF */}
      {tab === "bomchtref" ? (
        <div className="boCard">
          <div className="boSectionTitle" style={{ display: "flex", justifyContent: "space-between" }}>
            <span>BOM CHT REF (Changement de référence)</span>
            <button className="boBtn" onClick={() => addListRow(setBomChtRefRows, emptyBomRow)} disabled={locked}>
              + Ajouter ligne
            </button>
          </div>

          {/* <div className="boHint" style={{ marginBottom: 8 }}>
            Meme structure que BOM (Excel : colonnes Description... + MY12 / SUPPORT / MQT)
          </div> */}

          <div className="boTableWrap">
            <table className="boTable">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="right">Qte/carte</th>
                  <th>Ref fabricant</th>
                  <th>Fournisseur</th>
                  <th>Réf fournisseur</th>
                  <th>Assemblage</th>
                  <th>Repères</th>
                  <th className="right">Qté commandée</th>
                  <th className="center">MY12</th>
                  <th className="center">SUPPORT</th>
                  <th className="center">MQT</th>
                  <th className="right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {bomChtRefRows.map((r, i) => (
                  <tr key={i}>
                    <td><input className="boInput" value={r.description} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "description", e.target.value)} /></td>
                    <td className="right"><input className="boInput mono" value={r.qty_per_board} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "qty_per_board", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.mpn} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "mpn", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.supplier} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "supplier", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.supplier_ref} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "supplier_ref", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.assembly} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "assembly", e.target.value)} /></td>
                    <td><input className="boInput mono" value={r.refs} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "refs", e.target.value)} /></td>
                    <td className="right"><input className="boInput mono" value={r.qty_ordered} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "qty_ordered", e.target.value)} /></td>

                    <td className="center"><input type="checkbox" checked={!!r.my12} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "my12", e.target.checked)} /></td>
                    <td className="center"><input type="checkbox" checked={!!r.support} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "support", e.target.checked)} /></td>
                    <td className="center"><input type="checkbox" checked={!!r.mqt} disabled={locked} onChange={(e) => updateListRow(setBomChtRefRows, i, "mqt", e.target.checked)} /></td>

                    <td className="right">
                      <button className="boBtn boBtnGhost" onClick={() => removeListRow(setBomChtRefRows, i)} disabled={locked}>
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
                {!bomChtRefRows.length ? <tr><td colSpan={12} style={{ opacity: 0.8 }}>Aucune ligne</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* TAB: FICHE LIAISON */}
      {tab === "liaison" ? (
        <div className="boCard">
          <div className="boSectionTitle">FICHE LIAISON</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(240px, 1fr))", gap: 12 }}>
            <label className="boLabel">
              AUTEUR PROG P&amp;P
              <input className="boInput mono" value={liaison.auteur_prog_pp} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, auteur_prog_pp: e.target.value }))} />
            </label>
            <label className="boLabel">
              AUTEUR PROG JETTING
              <input className="boInput mono" value={liaison.auteur_prog_jetting} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, auteur_prog_jetting: e.target.value }))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint">JETTING CRITIQUE (reperes + % programme)</div>
            <textarea className="boInput" style={{ minHeight: 90, resize: "vertical" }} value={liaison.jetting_critique} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, jetting_critique: e.target.value }))} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint">Raison</div>
            <textarea className="boInput" style={{ minHeight: 70, resize: "vertical" }} value={liaison.raison} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, raison: e.target.value }))} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint">LISTE PREMOUNT</div>
            <textarea className="boInput" style={{ minHeight: 90, resize: "vertical" }} value={liaison.liste_premount} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, liste_premount: e.target.value }))} />
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint">OBSERVATIONS PRODUCTION</div>
            <textarea className="boInput" style={{ minHeight: 120, resize: "vertical" }} value={liaison.observations_production} disabled={locked} onChange={(e) => setLiaison((p) => ({ ...p, observations_production: e.target.value }))} />
          </div>
        </div>
      ) : null}

      {/* TAB: FICHE SAV */}
      {tab === "sav" ? (
        <div className="boCard">
          <div className="boSectionTitle">FICHE SAV</div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(260px, 1fr))", gap: 12 }}>
            <label className="boLabel">
              RECP CARTES
              <input className="boInput mono" value={sav.recp_cartes} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, recp_cartes: e.target.value }))} />
            </label>
            <label className="boLabel">
              DATE RECP (Cartes)
              <input className="boInput mono" value={sav.date_recp_cartes} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, date_recp_cartes: e.target.value }))} />
            </label>

            <label className="boLabel">
              RECP CPT
              <input className="boInput mono" value={sav.recp_cpt} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, recp_cpt: e.target.value }))} />
            </label>
            <label className="boLabel">
              DATE RECP (CPT)
              <input className="boInput mono" value={sav.date_recp_cpt} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, date_recp_cpt: e.target.value }))} />
            </label>

            <label className="boLabel">
              ANALYSE
              <input className="boInput mono" value={sav.analyse} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, analyse: e.target.value }))} />
            </label>
            <label className="boLabel">
              DATE ANALYSE
              <input className="boInput mono" value={sav.date_analyse} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, date_analyse: e.target.value }))} />
            </label>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="boHint">Notes / Details analyse (zone texte Excel)</div>
            <textarea className="boInput" style={{ minHeight: 140, resize: "vertical" }} value={sav.notes} disabled={locked} onChange={(e) => setSav((p) => ({ ...p, notes: e.target.value }))} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
